import React, { useEffect, useRef, useState, Suspense } from "react";
import PropTypes from "prop-types"
import queryString from 'query-string';
import Cookies from 'js-cookie';
import { firebaseConfig } from "../../firebaseConfig";
import { graphql, Link, navigate, useStaticQuery } from "gatsby"
import { lazyLoad } from "../utils/lazy-load";
// Import critical components directly
import AgeOverlay from "./age-overlay"
// Import critical UI components synchronously to ensure they're available immediately
import { OutboundLink } from 'gatsby-plugin-google-gtag';
// formatImageKey is not used in this component
import ResponsiveImage from "./responsive-image";
// Import only isBrowser synchronously as it's needed for initial rendering
import { isBrowser } from "../services/auth";

// Lazy load non-critical components
const Notice = lazyLoad(() => import(/* webpackChunkName: "notice" */ "./notice"));
const Status = lazyLoad(() => import(/* webpackChunkName: "status" */ "./status"));
const ProcessingOverlay = lazyLoad(() => import(/* webpackChunkName: "processing-overlay" */ "./pay/processing-overlay"));
const FreshdeskWidget = lazyLoad(() => import(/* webpackChunkName: "freshdesk" */ "./freshdesk.js"));

// Other auth and basket services will be dynamically imported in useEffect
const setUID = () => {
  // Dynamically import setUID when component mounts
  import(/* webpackChunkName: "basket-service" */ "../services/basket").then(({ setUID }) => {
    setUID();
  });
};

const Layout = ({ location, title, children }) => {
  // Defer setUID call until after initial render
  useEffect(() => {
    setUID();
  }, []);
  const mounted = useRef(false);
  const [firebase, setFirebase] = useState(null);
  const [signInSuccessUrl, setSignInSuccessUrl] = useState("/");
  const [consented, setConsented] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing ] = useState(queryString.parse(location.search).refs === '1');
  const [indexedDBSupported, setIndexedDBSupported] = useState(true);
  
  // Load Firebase dynamically
  useEffect(() => {
    // Load Firebase directly without using hooks
    import(/* webpackChunkName: "firebase-app" */ "firebase/app").then(firebaseApp => {
      setFirebase(firebaseApp.default);
    });
  }, []);

  const data = useStaticQuery(graphql`
    query Layout {
      site {
        siteMetadata {
          mastercardLogo
          title
          visaLogo
        }
      }
    }
  `)
  const siteTitle = data.site.siteMetadata.title
  // imageHost is no longer needed as ResponsiveImage handles this internally
  const { mastercardLogo, visaLogo }  = data.site.siteMetadata;

  useEffect(() => {
    mounted.current = true;
    
    // Check if we're on a Firebase default domain and redirect to the main domain if needed
    if (isBrowser()) {
      const hostname = window.location.hostname;
      if (hostname === 'water-and-power.firebaseapp.com' || hostname === 'water-and-power.web.app') {
        const path = window.location.pathname + window.location.search + window.location.hash;
        window.location.href = `https://water-and-power.com${path}`;
        return;
      }
    }
    
    // Dynamically import auth services only when needed
    import(/* webpackChunkName: "auth-services" */ "../services/auth").then(({ getGeo, getWithExpiry, isPremium, removeKey, setPremium }) => {
      if (!firebase) return;
      
      (async () => {
        const { supported } = await getGeo();
        setIndexedDBSupported(supported);
      })();

      setConsented(Cookies.get('consented') !== undefined)
      setLoading(false);
      const segpayConversion = getWithExpiry(`segpayConversion`);
      
      // Handle payment conversion
      if (isPremium() && segpayConversion) {
        // Dynamically import basket service
        import(/* webpackChunkName: "basket-service" */ "../services/basket").then(({ pageTitle, pageLocation, purchase }) => {
          const { title, path } = segpayConversion;
          pageTitle(title);
          pageLocation(`${location.origin}${path}`);
          purchase(segpayConversion);
          removeKey(`segpayConversion`);
        });
      }

      if (location.pathname) setSignInSuccessUrl(location.pathname);
      if (processing) {
        // Dynamically import Comlink only when processing
        import(/* webpackChunkName: "comlink" */ "comlink").then((ComlinkModule) => {
          // Need to call this explicitly here otherwise the timing doesn't work.
          const worker = new Worker("/workers/main.js");
          const obj = ComlinkModule.wrap(worker);

          (async () => {
            await obj.authenticate(firebaseConfig);
            const user = await obj.user
            if (mounted.current && user) {
              setPremium(user.premium);
              navigate(location.pathname, { replace: true });
            }
          })();
          setProcessing(false);
        });
      };
    });
    
    return () => mounted.current = false;
  }, [firebase, location, processing]);

  const baseClass = "rounded-full h-fit w-fit p-1 self-center text-white flex items-center hover:bg-black transition-all duration-300 ease-in-out"

  if (
    !indexedDBSupported
    ||
    (isBrowser() && !window.indexedDB)
  ) {
    return(
      <div className="container m-auto flex h-screen justify-center items-center">
        <div className="text-center px-2">
          <h1 className="w-full text-4xl font-bold mb-2">
            {siteTitle}
          </h1>
          <h2 className="w-full text-2xl font-bold mb-2">
            Your browser does not support indexedDB
          </h2>
          <p className="w-full text-xl">
            The site requires IndexedDB to run properly. Please re-enable it or try a different browser. All mainstream modern
            browsers currently support IndexedDB except Firefox running in private browsing mode.
          </p>
          <p className="w-full text-xl">
            The site will not run in Firefox's private browsing mode.
          </p>
        </div>
      </div>
    )
  } else if(loading === true) {
    return (
      <div className="container m-auto flex h-screen justify-center items-center">
        <div role="status">
          <svg aria-hidden="true" className="inline w-20 h-20 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
          </svg>
          <span className="sr-only">Water and Power is loading...</span>
        </div>
      </div>
    )
  } else {
    // TEMPORARILY DISABLED version notification padding
    return (
      <div className="container mx-auto flex flex-col h-screen justify-between pt-0">
        <AgeOverlay consented={ consented } />
        <ProcessingOverlay processing={ processing } />
        <header className="mb-2 relative bg-gray-600 md:pb-2 flex flex-row justify-between">
          <section className="w-auto">
            <h1 className="h-full md:h-fit leading-tight md:leading-normal text-2xl md:text-4xl font-bold flex items-center ml-2" data-lcp-element="true">
              <a href="/" className="text-current font-bold" title="Home" aria-label="Return to homepage" rel="home" fetchpriority="high" loading="eager">
                {siteTitle}
              </a>
            </h1>
            <h2 className="ml-4 hidden md:inline-block text-xl">
              The best BDSM enema and inflation porn in the world!
            </h2>
          </section>
        </header>
        <Suspense fallback={<div className="p-4 text-center">Loading notice...</div>}>
          <Notice />
        </Suspense>
        <div className="h-fit bg-gray-600 mt-2 flex flex-row justify-end">
          <Link
            to="/faq"
            activeClassName="bg-black"
            className={ baseClass }
          >
            <svg
              role="button"
              className="FAQIcon w-8 h-8"
              viewBox="0 0 24 24">
              <path fill="currentColor" d="M18,15H6L2,19V3A1,1 0 0,1 3,2H18A1,1 0 0,1 19,3V14A1,1 0 0,1 18,15M23,9V23L19,19H8A1,1 0 0,1 7,18V17H21V8H22A1,1 0 0,1 23,9M8.19,4C7.32,4 6.62,4.2 6.08,4.59C5.56,5 5.3,5.57 5.31,6.36L5.32,6.39H7.25C7.26,6.09 7.35,5.86 7.53,5.7C7.71,5.55 7.93,5.47 8.19,5.47C8.5,5.47 8.76,5.57 8.94,5.75C9.12,5.94 9.2,6.2 9.2,6.5C9.2,6.82 9.13,7.09 8.97,7.32C8.83,7.55 8.62,7.75 8.36,7.91C7.85,8.25 7.5,8.55 7.31,8.82C7.11,9.08 7,9.5 7,10H9C9,9.69 9.04,9.44 9.13,9.26C9.22,9.08 9.39,8.9 9.64,8.74C10.09,8.5 10.46,8.21 10.75,7.81C11.04,7.41 11.19,7 11.19,6.5C11.19,5.74 10.92,5.13 10.38,4.68C9.85,4.23 9.12,4 8.19,4M7,11V13H9V11H7M13,13H15V11H13V13M13,4V10H15V4H13Z" />
            </svg>
          </Link>
          <Link
            to="/search"
            activeClassName="bg-black"
            className={ baseClass }
            data-testid="search-icon-header"
          >
            <svg
              role="button"
              className="SearchIcon w-8 h-8"
              viewBox="0 0 24 24">
              <path fill="currentColor" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
            </svg>
          </Link>
          <Link
            to="/categories"
            activeClassName="bg-black"
            id="tags-link"
            className={ baseClass }
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path fill="currentColor" d="M5.5,9A1.5,1.5 0 0,0 7,7.5A1.5,1.5 0 0,0 5.5,6A1.5,1.5 0 0,0 4,7.5A1.5,1.5 0 0,0 5.5,9M17.41,11.58C17.77,11.94 18,12.44 18,13C18,13.55 17.78,14.05 17.41,14.41L12.41,19.41C12.05,19.77 11.55,20 11,20C10.45,20 9.95,19.78 9.58,19.41L2.59,12.42C2.22,12.05 2,11.55 2,11V6C2,4.89 2.89,4 4,4H9C9.55,4 10.05,4.22 10.41,4.58L17.41,11.58M13.54,5.71L14.54,4.71L21.41,11.58C21.78,11.94 22,12.45 22,13C22,13.55 21.78,14.05 21.42,14.41L16.04,19.79L15.04,18.79L20.75,13L13.54,5.71Z" />
            </svg>
          </Link>
          <Link
            to="/models"
            activeClassName="bg-black"
            id="girls-link"
            className={ baseClass }
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path fill="currentColor" d="M13.75 13C13.75 12.31 14.31 11.75 15 11.75S16.25 12.31 16.25 13 15.69 14.25 15 14.25
                13.75 13.69 13.75 13M22 12V22H2V12C2 6.5 6.5 2 12 2S22 6.5 22 12M4 12C4 16.41 7.59 20 12 20S20 16.41 20 12C20 11.21
                19.88 10.45 19.67 9.74C18.95 9.91 18.2 10 17.42 10C14.05 10 11.07 8.33 9.26 5.77C8.28 8.16 6.41 10.09 4.05 11.14C4 11.42 4
                11.71 4 12M9 14.25C9.69 14.25 10.25 13.69 10.25 13S9.69 11.75 9 11.75 7.75 12.31 7.75 13 8.31 14.25 9 14.25Z" />
            </svg>
          </Link>
          <Status signInSuccessUrl={signInSuccessUrl} title={ title }/>
        </div>
        <main className="mb-auto">{children}</main>
        <div className="h-fit bg-gray-600 mt-2 flex flex-row justify-end">
          <Link
            to="/faq"
            activeClassName="bg-black"
            className={ baseClass }
          >
            <svg
              role="button"
              className="FAQIcon w-8 h-8"
              viewBox="0 0 24 24">
              <path fill="currentColor" d="M18,15H6L2,19V3A1,1 0 0,1 3,2H18A1,1 0 0,1 19,3V14A1,1 0 0,1 18,15M23,9V23L19,19H8A1,1 0 0,1 7,18V17H21V8H22A1,1 0 0,1 23,9M8.19,4C7.32,4 6.62,4.2 6.08,4.59C5.56,5 5.3,5.57 5.31,6.36L5.32,6.39H7.25C7.26,6.09 7.35,5.86 7.53,5.7C7.71,5.55 7.93,5.47 8.19,5.47C8.5,5.47 8.76,5.57 8.94,5.75C9.12,5.94 9.2,6.2 9.2,6.5C9.2,6.82 9.13,7.09 8.97,7.32C8.83,7.55 8.62,7.75 8.36,7.91C7.85,8.25 7.5,8.55 7.31,8.82C7.11,9.08 7,9.5 7,10H9C9,9.69 9.04,9.44 9.13,9.26C9.22,9.08 9.39,8.9 9.64,8.74C10.09,8.5 10.46,8.21 10.75,7.81C11.04,7.41 11.19,7 11.19,6.5C11.19,5.74 10.92,5.13 10.38,4.68C9.85,4.23 9.12,4 8.19,4M7,11V13H9V11H7M13,13H15V11H13V13M13,4V10H15V4H13Z" />
            </svg>
          </Link>
          <Link
            to="/search"
            activeClassName="bg-black"
            className={ baseClass }
            data-testid="search-icon"
          >
            <svg
              role="button"
              className="SearchIcon w-8 h-8"
              viewBox="0 0 24 24">
              <path fill="currentColor" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
            </svg>
          </Link>
          <Link
            to="/categories"
            activeClassName="bg-black"
            id="tags-link"
            className={ baseClass }
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path fill="currentColor" d="M5.5,9A1.5,1.5 0 0,0 7,7.5A1.5,1.5 0 0,0 5.5,6A1.5,1.5 0 0,0 4,7.5A1.5,1.5 0 0,0 5.5,9M17.41,11.58C17.77,11.94 18,12.44 18,13C18,13.55 17.78,14.05 17.41,14.41L12.41,19.41C12.05,19.77 11.55,20 11,20C10.45,20 9.95,19.78 9.58,19.41L2.59,12.42C2.22,12.05 2,11.55 2,11V6C2,4.89 2.89,4 4,4H9C9.55,4 10.05,4.22 10.41,4.58L17.41,11.58M13.54,5.71L14.54,4.71L21.41,11.58C21.78,11.94 22,12.45 22,13C22,13.55 21.78,14.05 21.42,14.41L16.04,19.79L15.04,18.79L20.75,13L13.54,5.71Z" />
            </svg>
          </Link>
          <Link
            to="/models"
            activeClassName="bg-black"
            id="girls-link"
            className={ baseClass }
          >
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path fill="currentColor" d="M13.75 13C13.75 12.31 14.31 11.75 15 11.75S16.25 12.31 16.25 13 15.69 14.25 15 14.25
                13.75 13.69 13.75 13M22 12V22H2V12C2 6.5 6.5 2 12 2S22 6.5 22 12M4 12C4 16.41 7.59 20 12 20S20 16.41 20 12C20 11.21
                19.88 10.45 19.67 9.74C18.95 9.91 18.2 10 17.42 10C14.05 10 11.07 8.33 9.26 5.77C8.28 8.16 6.41 10.09 4.05 11.14C4 11.42 4
                11.71 4 12M9 14.25C9.69 14.25 10.25 13.69 10.25 13S9.69 11.75 9 11.75 7.75 12.31 7.75 13 8.31 14.25 9 14.25Z" />
            </svg>
          </Link>
          <Status signInSuccessUrl={signInSuccessUrl} title={ title }/>
        </div>
        <footer className="flex flex-row flex-wrap items-center space-x-2">
          <Link
            to="/"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
          >
            © {new Date().getFullYear()}
            <span className="inline md:hidden">
              &nbsp;
              WaP
            </span>
            <span className="hidden md:inline">
              &nbsp;
              Water and Power,
              71-75 Shelton Street, London, WC2H 9JQ
            </span>
          </Link>
          <div>
            |
          </div>
          <Link
            to="/faq"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
          >
            FAQ
          </Link>
          <Link
            to="/record-keeping"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
          >
            2257
          </Link>
          <Link
            to="/terms"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
          >
            Terms
          </Link>
          <Link
            to="/privacy"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
          >
            Privacy
          </Link>
          <Link
            to="/cookies"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
          >
            Cookies
          </Link>
          <Link
            to="/model"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
          >
            Model Call
          </Link>
          <Link
            to="/support"
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
          >
            Support
          </Link>
          <OutboundLink
            className="hover:bg-gray-300 hover:text-black rounded-md px-1"
            activeClassName="bg-blue-800 text-gray-300 hover:bg-gray-800 hover:text-black"
            href="https://form.jotform.com/242135704837053">
            Content Removal
          </OutboundLink>
          <div>
            |
          </div>
          <ResponsiveImage
            src={visaLogo}
            alt="Visa logo"
            className="w-10 h-fit"
            layout="thumbnail"
            maintainAspectRatio={false}
          />
          <ResponsiveImage
            src={mastercardLogo}
            alt="Mastercard logo"
            className="w-10 h-fit"
            layout="thumbnail"
            maintainAspectRatio={false}
          />
        </footer>
        {/* FreshdeskWidget moved to end of page to improve LCP performance */}
        <Suspense fallback={null}>
          <FreshdeskWidget />
        </Suspense>
      </div>
    )
  }
}

Layout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object
  ]).isRequired,
  location: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
}

export default Layout
